import { call, put, select, takeEvery } from "redux-saga/effects";
import moment from "moment-timezone";
import { getDataFromSxi, getJwtToken } from "api";
import { ENDPOINT, PICTREE } from "../../constants";
import {
  setPageTopLoader,
  setBudgetSummaryDataTepco,
  setBudgetSummaryDataNtt,
  setBudgetSummaryDataNttFt,
  setBudgetSummaryDataHokuden,
  setBudgetRemainTrendDataTepco,
  setBudgetRemainTrendDataNtt,
  setBudgetRemainTrendDataNttFt,
  setBudgetRemainTrendDataHokuden,
} from "actions";

const REGULAR_SEASON_KEYWORDS = ["レギュラーシーズン", "regular season"];

const BUDGET_TREND_CAPITAL_BUDGET_IDS = {
  tepco: [
    "cb_mig_cmn8t73ia0001jmpr4eig3uah",
    "cb_mig_cmn8twz7r0001jlpr7yotbqg3",
    "cb_mig_cmn8tzajq0001xhpr5huq9xdp",
  ],
  ntt: ["capital_budget_cmp4yz2lj00017rtcc4ok0jgv"],
  "ntt-ft": [],
  hokuden: [],
};

const BUDGET_TREND_METADATA = {
  tepco: {
    "cb_mig_cmn8t73ia0001jmpr4eig3uah": {
      code: "CHIBA1",
      name: "ツルツタ千葉1",
      color: "#3F7ECC",
    },
    "cb_mig_cmn8twz7r0001jlpr7yotbqg3": {
      code: "IBARAKI1",
      name: "ツルツタ茨城1",
      color: "#E4961A",
    },
    "cb_mig_cmn8tzajq0001xhpr5huq9xdp": {
      code: "SAITAMA1",
      name: "ツルツタ埼玉1",
      color: "#D45F2D",
    },
  },
  ntt: {
    "capital_budget_cmp4yz2lj00017rtcc4ok0jgv": {
      code: "RS",
      name: "レギュラーS",
      color: "#3F7ECC",
    },
  },
};

const FALLBACK_COLORS = ["#3F7ECC", "#E4961A", "#D45F2D", "#5E8BFF", "#77B255", "#A95AA1"];

const getBudgetSummarySetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetSummaryDataNtt;
    case "ntt-ft":
      return setBudgetSummaryDataNttFt;
    case "hokuden":
      return setBudgetSummaryDataHokuden;
    case "tepco":
    default:
      return setBudgetSummaryDataTepco;
  }
};

const getBudgetTrendSetter = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return setBudgetRemainTrendDataNtt;
    case "ntt-ft":
      return setBudgetRemainTrendDataNttFt;
    case "hokuden":
      return setBudgetRemainTrendDataHokuden;
    case "tepco":
    default:
      return setBudgetRemainTrendDataTepco;
  }
};

const getBudgetSelectors = (companyCode) => {
  switch (companyCode) {
    case "ntt":
      return {
        selectedSeason: (state) => state.pictreeNtt.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeNtt,
      };
    case "ntt-ft":
      return {
        selectedSeason: (state) => state.pictreeNttFt.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeNttFt,
      };
    case "hokuden":
      return {
        selectedSeason: (state) => state.pictreeHokuden.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTreeHokuden,
      };
    case "tepco":
    default:
      return {
        selectedSeason: (state) => state.pictree.selectedSeason,
        dateRange: (state) => state.page.dateRangePicTree,
      };
  }
};

const BUDGET_ASSET_JOIN_CONFIG = {
  tepco: {
    detailTable: "power_asset_detail_of_tepco",
    detailAlias: "pad",
  },
  ntt: {
    detailTable: "power_asset_detail_of_ntt_me",
    detailAlias: "pad",
  },
  "ntt-ft": {
    detailTable: "power_asset_detail_of_ntt_ft",
    detailAlias: "pad",
  },
  hokuden: {
    detailTable: "power_asset_detail_of_hepco",
    detailAlias: "pad",
  },
};

const BUDGET_QUERY_CONFIG = {
  tepco: {
    multiCompany: false,
  },
  ntt: {
    multiCompany: false,
  },
  "ntt-ft": {
    multiCompany: false,
  },
  hokuden: {
    multiCompany: false,
  },
};

const buildBudgetAssetJoinClause = (companyCode) => {
  const config = BUDGET_ASSET_JOIN_CONFIG[companyCode];

  const baseJoin = `
      JOIN power_asset pa
        ON pa.id = gspab.power_asset_id
  `;

  if (!config) {
    return baseJoin;
  }

  return `${baseJoin}
      JOIN ${config.detailTable} ${config.detailAlias}
        ON ${config.detailAlias}.power_asset_id = pa.id`;
};

const shouldJoinBudgetAssetDetail = (companyCode) => {
  const config = BUDGET_QUERY_CONFIG[companyCode];
  return config ? config.multiCompany !== false : true;
};

const isRegularSeason = (season) => {
  const seasonName = String(season?.seasonName || "");
  return REGULAR_SEASON_KEYWORDS.some((keyword) => seasonName.includes(keyword));
};

const buildGameSpaceCondition = (selectedSeasonData) => {
  const seasons = Array.isArray(selectedSeasonData) ? selectedSeasonData : [];
  const selectedSeasonIds = seasons
    .map((season) => season && season.seasonId)
    .filter(Boolean);
  const hasRegularSeason = seasons.some(isRegularSeason);
  const nonRegularSeasonIds = selectedSeasonIds.filter((id) => {
    const matched = seasons.find((season) => season && season.seasonId === id);
    return !isRegularSeason(matched);
  });

  const clauses = [];
  if (hasRegularSeason) {
    clauses.push("gspab.game_space_id IS NULL");
  }
  if (nonRegularSeasonIds.length > 0) {
    const idList = nonRegularSeasonIds.map((id) => `'${id}'`).join(", ");
    clauses.push(`gspab.game_space_id IN (${idList})`);
  }

  if (clauses.length === 0) {
    return null;
  }

  return clauses.length === 1
    ? `AND ${clauses[0]}`
    : `AND (${clauses.join(" OR ")})`;
};

const buildUtcRange = (dateRange) => {
  if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
    return null;
  }

  return {
    startUtc: moment
      .tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss"),
    endUtc: moment
      .tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss"),
  };
};

const getBudgetTrendIds = (companyCode) => BUDGET_TREND_CAPITAL_BUDGET_IDS[companyCode] || [];

const getBudgetTrendMeta = (companyCode, capitalBudgetId, index) => {
  const companyMeta = BUDGET_TREND_METADATA[companyCode] || {};
  const fallbackCode = capitalBudgetId ? String(capitalBudgetId).slice(-6).toUpperCase() : `FUND${index + 1}`;
  const fallbackColor = FALLBACK_COLORS[index % FALLBACK_COLORS.length];

  return (
    companyMeta[capitalBudgetId] || {
      code: fallbackCode,
      name: capitalBudgetId || `原資${index + 1}`,
      color: fallbackColor,
    }
  );
};

const toMillionValue = (amount) => Number((Number(amount || 0) / 1000000).toFixed(1));

const extractSingleRowValue = (rows, key) => {
  const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  return Number(row?.[key] || 0);
};

const getRangeDates = (dateRange) => {
  if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
    return [];
  }

  const start = moment.tz(dateRange[0], "Asia/Tokyo").startOf("day");
  const end = moment.tz(dateRange[1], "Asia/Tokyo").startOf("day");

  const dates = [];
  for (let cursor = start.clone(); cursor.isSameOrBefore(end, "day"); cursor.add(1, "day")) {
    dates.push(cursor.format("YYYY-MM-DD"));
  }

  return dates;
};

const buildBudgetSummaryTotalQuery = (selectedSeasonData) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);

  if (!seasonCondition) {
    return null;
  }

  return {
    query: `
      SELECT COALESCE(SUM(pab.initial_balance_amount), 0) AS total_budget_amount
      FROM game_space_power_asset_budget gspab
      JOIN game_space_power_asset_budget_budget_bind gspabb
        ON gspabb.asset_budget_id = gspab.id
      JOIN power_asset_budget pab
        ON pab.id = gspabb.budget_id
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetSummaryConsumedQuery = (companyCode, selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);
  const utcRange = buildUtcRange(dateRange);

  if (!seasonCondition || !utcRange) {
    return null;
  }

  return {
    query: `
      SELECT
        COALESCE(SUM(pabeorg.reward_amount), 0) AS consumed_budget_amount
      FROM game_space_power_asset_budget gspab
      JOIN game_space_power_asset_budget_budget_bind gspabb
        ON gspabb.asset_budget_id = gspab.id
      JOIN power_asset_budget pab
        ON pab.id = gspabb.budget_id
      JOIN power_asset_budget_event pabe
        ON pabe.budget_id = pab.id
      JOIN power_asset_budget_event_of_reward_granted pabeorg
        ON pabeorg.id = pabe.id
      ${shouldJoinBudgetAssetDetail(companyCode) ? buildBudgetAssetJoinClause(companyCode) : ""}
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
        AND pabe.occurred_datetime >= '${utcRange.startUtc}'
        AND pabe.occurred_datetime < '${utcRange.endUtc}'
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetSummaryRemainingQuery = (companyCode, selectedSeasonData) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);

  if (!seasonCondition) {
    return null;
  }

  return {
    query: `
      SELECT
        COALESCE(SUM(pab.balance_amount), 0) AS remaining_budget_amount
      FROM game_space_power_asset_budget gspab
      JOIN game_space_power_asset_budget_budget_bind gspabb
        ON gspabb.asset_budget_id = gspab.id
      JOIN power_asset_budget pab
        ON pab.id = gspabb.budget_id
      ${shouldJoinBudgetAssetDetail(companyCode) ? buildBudgetAssetJoinClause(companyCode) : ""}
      WHERE ${seasonCondition.replace(/^AND\s+/, "")}
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildBudgetRemainTrendQuery = (companyCode, selectedSeasonData, dateRange) => {
  const seasonCondition = buildGameSpaceCondition(selectedSeasonData);
  const utcRange = buildUtcRange(dateRange);
  const endUtc = utcRange?.endUtc;
  const startUtc = utcRange?.startUtc;
  const capitalBudgetIds = getBudgetTrendIds(companyCode);
  const capitalBudgetCondition = capitalBudgetIds.length > 0
    ? `AND pab.capital_budget_id IN (${capitalBudgetIds.map((id) => `'${id}'`).join(", ")})`
    : "";
  const budgetAssetJoinClause = shouldJoinBudgetAssetDetail(companyCode)
    ? buildBudgetAssetJoinClause(companyCode)
    : "";

  if (!seasonCondition || !startUtc || !endUtc) {
    return null;
  }

  return {
    query: `
      WITH RECURSIVE date_series AS (
        SELECT DATE(CONVERT_TZ('${startUtc}', 'UTC', 'Asia/Tokyo')) AS target_date
        UNION ALL
        SELECT DATE_ADD(target_date, INTERVAL 1 DAY)
        FROM date_series
        WHERE target_date < DATE(CONVERT_TZ('${endUtc}', 'UTC', 'Asia/Tokyo'))
      ),
      target_budget AS (
        SELECT DISTINCT
          pab.id AS budget_id,
          pab.capital_budget_id,
          pab.initial_balance_amount
        FROM game_space_power_asset_budget gspab
        JOIN game_space_power_asset_budget_budget_bind gspabb
          ON gspabb.asset_budget_id = gspab.id
        JOIN power_asset_budget pab
          ON pab.id = gspabb.budget_id
        ${budgetAssetJoinClause}
        WHERE ${seasonCondition.replace(/^AND\s+/, "")}
          ${capitalBudgetCondition}
      ),
      budget_total AS (
        SELECT
          capital_budget_id,
          SUM(initial_balance_amount) AS total_initial_balance_amount
        FROM target_budget
        GROUP BY capital_budget_id
      ),
      daily_reward AS (
        SELECT
          tb.capital_budget_id,
          DATE(CONVERT_TZ(pabe.occurred_datetime, 'UTC', 'Asia/Tokyo')) AS target_date,
          SUM(pabeorg.reward_amount) AS daily_reward_amount
        FROM target_budget tb
        JOIN power_asset_budget_event pabe
          ON pabe.budget_id = tb.budget_id
         AND pabe.occurred_datetime < '${endUtc}'
        JOIN power_asset_budget_event_of_reward_granted pabeorg
          ON pabeorg.id = pabe.id
        GROUP BY
          tb.capital_budget_id,
          DATE(CONVERT_TZ(pabe.occurred_datetime, 'UTC', 'Asia/Tokyo'))
      ),
      budget_keys AS (
        SELECT capital_budget_id
        FROM budget_total
      ),
      date_budget AS (
        SELECT
          bk.capital_budget_id,
          ds.target_date
        FROM budget_keys bk
        CROSS JOIN date_series ds
      ),
      daily_running AS (
        SELECT
          db.capital_budget_id,
          db.target_date,
          COALESCE(dr.daily_reward_amount, 0) AS daily_reward_amount,
          SUM(COALESCE(dr.daily_reward_amount, 0)) OVER (
            PARTITION BY db.capital_budget_id
            ORDER BY db.target_date
            ROWS UNBOUNDED PRECEDING
          ) AS cumulative_reward_amount
        FROM date_budget db
        LEFT JOIN daily_reward dr
          ON dr.capital_budget_id = db.capital_budget_id
         AND dr.target_date = db.target_date
      )
      SELECT
        dr.capital_budget_id,
        dr.target_date,
        bt.total_initial_balance_amount - dr.cumulative_reward_amount AS remaining_amount
      FROM daily_running dr
      JOIN budget_total bt
        ON bt.capital_budget_id = dr.capital_budget_id
      ORDER BY dr.capital_budget_id, dr.target_date
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const fetchRows = function* (query) {
  const jwtToken = yield call(getJwtToken);
  return yield call(getDataFromSxi, query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken);
};

const normalizeBudgetTrend = (rows, companyCode, selectedDateRange) => {
  const dateList = getRangeDates(selectedDateRange);
  const groupedByBudget = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const capitalBudgetId = row?.capital_budget_id || null;
    if (!capitalBudgetId) return;

    if (!groupedByBudget.has(capitalBudgetId)) {
      groupedByBudget.set(capitalBudgetId, {
        capitalBudgetId,
        days: new Map(),
      });
    }
    const group = groupedByBudget.get(capitalBudgetId);

    if (row?.target_date) {
      const targetDate = moment.utc(row.target_date).tz("Asia/Tokyo").format("YYYY-MM-DD");
      group.days.set(targetDate, Number(row?.remaining_amount || 0));
    }
  });

  const orderedBudgetIds = [
    ...getBudgetTrendIds(companyCode),
    ...Array.from(groupedByBudget.keys()).filter((id) => !getBudgetTrendIds(companyCode).includes(id)),
  ].filter((id, index, self) => self.indexOf(id) === index);

  const trend = { date: dateList.map((date) => moment(date).format("M/D")) };
  const chartConfig = {};
  const legend = [];
  const colorValue = [];
  const bySourceRows = [];

  orderedBudgetIds.forEach((capitalBudgetId, index) => {
    const group = groupedByBudget.get(capitalBudgetId);
    if (!group) return;

    const meta = getBudgetTrendMeta(companyCode, capitalBudgetId, index);
    const seriesKey = `count_${String(capitalBudgetId).replace(/[^a-zA-Z0-9]/g, "_")}`;
    const values = dateList.map((dateKey) => {
      const remainingAmount = group.days.get(dateKey);
      return remainingAmount === undefined ? null : toMillionValue(remainingAmount);
    });

    trend[seriesKey] = values;
    chartConfig[seriesKey] = meta.name;
    legend.push(meta.name);
    colorValue.push(meta.color);

    const lastRemainingAmount = [...group.days.entries()].length > 0
      ? [...group.days.entries()]
        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        .reduce((_, [, remainingAmount]) => remainingAmount, null)
      : null;

    bySourceRows.push({
      code: meta.code,
      name: meta.name,
      budget: "¥--",
      used: "¥--",
      remaining: lastRemainingAmount === null ? "¥--" : `¥${(lastRemainingAmount / 1000000).toFixed(1)}M`,
      progress: 0,
      color: meta.color,
    });
  });

  return {
    trend,
    legend,
    chartConfig,
    colorValue,
    bySourceRows,
  };
};

const buildEmptyTrendPayload = () => ({
  trend: { date: [] },
  legend: [],
  chartConfig: {},
  colorValue: [],
  bySourceRows: [],
});

// eslint-disable-next-line no-unused-vars
function* handleBudgetSummaryData(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setSummary = getBudgetSummarySetter(companyCode);

  yield put(setPageTopLoader(true));

  try {
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setSummary(null));
      return;
    }

    const totalQuery = buildBudgetSummaryTotalQuery(selectedSeasonData);
    const consumedQuery = buildBudgetSummaryConsumedQuery(companyCode, selectedSeasonData, selectedDateRange);
    const remainingQuery = buildBudgetSummaryRemainingQuery(companyCode, selectedSeasonData);

    if (!totalQuery || !consumedQuery || !remainingQuery) {
      yield put(setSummary(null));
      return;
    }

    const [totalRows, consumedRows, remainingRows] = [
      yield* fetchRows(totalQuery),
      yield* fetchRows(consumedQuery),
      yield* fetchRows(remainingQuery),
    ];

    const totalBudgetAmount = extractSingleRowValue(totalRows, "total_budget_amount");
    const consumedBudgetAmount = extractSingleRowValue(consumedRows, "consumed_budget_amount");
    const remainingBudgetAmount = extractSingleRowValue(remainingRows, "remaining_budget_amount");
    const consumedRate =
      totalBudgetAmount > 0
        ? Number(((consumedBudgetAmount / totalBudgetAmount) * 100).toFixed(1))
        : null;

    yield put(
      setSummary({
        totalBudgetAmount,
        consumedBudgetAmount,
        consumedRate,
        remainingBudgetAmount,
      })
    );
  } finally {
    yield put(setPageTopLoader(false));
  }
}

function* handleBudgetRemainTrend(action) {
  const companyCode = String(action?.payload?.companyCode || "tepco");
  const { selectedSeason, dateRange } = getBudgetSelectors(companyCode);
  const setTrend = getBudgetTrendSetter(companyCode);

  try {
    const selectedSeasonData = yield select(selectedSeason);
    const selectedDateRange = yield select(dateRange);

    if (!Array.isArray(selectedSeasonData) || selectedSeasonData.length === 0) {
      yield put(setTrend(buildEmptyTrendPayload()));
      return;
    }

    const trendQuery = buildBudgetRemainTrendQuery(companyCode, selectedSeasonData, selectedDateRange);

    if (!trendQuery) {
      yield put(setTrend(buildEmptyTrendPayload()));
      return;
    }

    const trendRows = yield* fetchRows(trendQuery);
    const normalizedTrend = normalizeBudgetTrend(trendRows, companyCode, selectedDateRange);

    yield put(setTrend(normalizedTrend));
  } catch (error) {
    console.log(error);
    yield put(setTrend(buildEmptyTrendPayload()));
  }
}

export default function* pictreeBudgetSaga() {
  yield takeEvery(PICTREE.GET_BUDGET_SUMMARY_DATA, handleBudgetSummaryData);
  yield takeEvery(PICTREE.GET_BUDGET_REMAIN_TREND, handleBudgetRemainTrend);
}
